import {
  Button,
  Card,
  Col,
  Form,
  message,
  Row,
  Select,
  Space,
  theme,
} from 'antd';
import React, { useEffect } from 'react';
import {
  AddSupervisorsToDepartment,
  GetSupervisorsNotInDepartment,
  GetUniversityDepartmentsOfDepartmentRepresentative,
  InviteSupervisorsToDepartment,
} from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import { validateMultiEmail } from '../../Validators/ValidatorEmail';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { DepartmentsAndSupervisorsCollapse } from './DepartmentsAndSupervisorsCollapse';

export const SupervisorManagementComponent = (): React.ReactElement => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const loggedInUser = useCurrentUser();
  const selectedDepartmentId = Form.useWatch('selectDepartment', form);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { token } = theme.useToken();

  const { data: departments } = useQuery({
    queryKey: [...QUERY_KEYS.DEPARTMENTS, loggedInUser?.id],
    queryFn: async () => {
      if (!loggedInUser?.id) return null;
      return await GetUniversityDepartmentsOfDepartmentRepresentative(
        loggedInUser.id.toString()
      );
    },
    enabled: !!loggedInUser?.id,
    meta: {
      onError: () => {
        messageApi.error(t('supvisManComp.fetchDepsError'));
      },
    },
  });

  const { data: supervisors, isLoading: loadingSupervisors } = useQuery({
    queryKey: [
      ...QUERY_KEYS.SUPERVISOR_NOT_IN_DEPARTMENT,
      selectedDepartmentId,
    ],
    queryFn: async () => {
      return await GetSupervisorsNotInDepartment(selectedDepartmentId);
    },
    enabled: !!selectedDepartmentId,
    meta: {
      onError: () => {
        messageApi.error(t('supvisManComp.fetchSupVisError'));
      },
    },
  });

  // Fetch departments on component mount
  useEffect(() => {
    if (departments) {
      form.setFieldValue('selectDepartment', departments[0].id);
    }
  }, [departments, form]);

  // Fetch supervisors when department changes
  useEffect(() => {
    if (!selectedDepartmentId) {
      form.resetFields(['selectInternalUsers']);
    }
  }, [form, selectedDepartmentId]);

  const clearFormFields = (fields?: string[]): void => {
    form.resetFields(fields);
  };

  const addSupervisorsToDepartment = async (): Promise<void> => {
    try {
      if (!selectedDepartmentId) {
        messageApi.info(t('supvisManComp.selDepFirst'));
        return;
      }

      if (!supervisors || supervisors.length === 0) {
        messageApi.warning(t('supvisManComp.noSupVis'));
        return;
      }

      const selectedSupervisorIds = form.getFieldValue('selectInternalUsers');
      if (!selectedSupervisorIds || selectedSupervisorIds.length === 0) {
        messageApi.info(t('supvisManComp.noSupVisSelected'));
        return;
      }

      await AddSupervisorsToDepartment(
        selectedDepartmentId,
        selectedSupervisorIds
      );
      clearFormFields(['selectInternalUsers']);
      messageApi.success(t('supvisManComp.addSupVisSuccess'));

      // Refresh supervisors list after adding
      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.SUPERVISOR_NOT_IN_DEPARTMENT,
          selectedDepartmentId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DEPARTMENTS_WITH_SUPERVISORS,
      });
    } catch (error) {
      console.log(error);
      messageApi.error(t('supvisManComp.addSupVisError'));
    }
  };

  const inviteSupervisorsToDepartment = async (): Promise<void> => {
    try {
      await form.validateFields(['emails']);

      if (!selectedDepartmentId) {
        messageApi.info(t('supvisManComp.selDepFirst'));
        return;
      }

      const supervisorEmails = form.getFieldValue('emails');
      if (!supervisorEmails || supervisorEmails.length === 0) {
        messageApi.info(t('supvisManComp.typeEmailToInvite'));
        return;
      }

      await InviteSupervisorsToDepartment(
        selectedDepartmentId,
        supervisorEmails
      );
      clearFormFields(['emails']);
      messageApi.success(t('supvisManComp.inviteSuccess'));

      // invalidate user to query refresh global user data
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DEPARTMENTS_WITH_SUPERVISORS,
      });
    } catch (error) {
      console.log(error);
      messageApi.error(t('supvisManComp.inviteError'));
    }
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: number }
  ): boolean => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size={50} style={{ width: '100%' }}>
        <Form form={form} size="middle">
          <Space direction="vertical" size="large" style={{ display: 'flex' }}>
            <Card style={{ boxShadow: token.boxShadow }}>
              <Row>
                <Col span={24}>
                  <Form.Item name="selectDepartment">
                    <Select
                      style={{ minWidth: '100%' }}
                      showSearch
                      allowClear
                      placeholder={t('supvisManComp.selDep')}
                      filterOption={filterOption}
                      options={departments?.map((dep) => ({
                        label: dep.name,
                        value: dep.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  <Form.Item name="selectInternalUsers">
                    <Select
                      style={{ minWidth: '100%' }}
                      showSearch
                      allowClear
                      mode="multiple"
                      placeholder={t('supvisManComp.selSupVisAdd')}
                      filterOption={filterOption}
                      loading={loadingSupervisors}
                      disabled={!selectedDepartmentId}
                      options={supervisors?.map((supvis) => ({
                        label:
                          `${supvis.lastName} ${supvis.firstName} (${supvis.email})`.trim(),
                        value: supvis.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      style={{ width: '100%' }}
                      onClick={addSupervisorsToDepartment}
                      disabled={!selectedDepartmentId}
                    >
                      {t('supvisManComp.addToDep')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card style={{ boxShadow: token.boxShadow }}>
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="emails"
                    rules={[{ validator: validateMultiEmail }]}
                  >
                    <Select
                      mode="tags"
                      style={{ minWidth: '100%' }}
                      allowClear
                      placeholder={t('supvisManComp.typeEmailToInvite')}
                      tokenSeparators={[',', ' ']}
                      disabled={!selectedDepartmentId}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      style={{ width: '100%' }}
                      onClick={inviteSupervisorsToDepartment}
                      disabled={!selectedDepartmentId}
                    >
                      {t('supvisManComp.invToDep')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Space>
        </Form>

        <DepartmentsAndSupervisorsCollapse />
      </Space>
    </>
  );
};
